/// <amd-module name="VSSUI/Components/Tabs/Tabs" />
import * as React from "react";

import { ITabProps, ITabsProps, Orientation, TabSize } from "VSSUI/Components/Tabs/Tabs.Props";

import { IObservableValue, ObservableLike } from "VSS/Core/Observable";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { css } from "VSSUI/Util";
import { Tab } from "./Tab";

/**
 * Renders all children as focusable, selectable elements and indicates whether or not they are
 * currently selected.
 */
export class Tabs extends React.Component<ITabsProps> {
    public constructor(props: ITabsProps) {
        super(props);

        if (ObservableLike.isObservable(props.selectedTabId)) {
            (props.selectedTabId as IObservableValue<string>).subscribe(this.onSelectedTabIdChanged);
        }
    }

    public componentWillUnmount() {
        if (ObservableLike.isObservable(this.props.selectedTabId)) {
            (this.props.selectedTabId as IObservableValue<string>).unsubscribe(this.onSelectedTabIdChanged);
        }
    }

    public render(): JSX.Element {
        const { tabSize = TabSize.Tall, orientation = Orientation.Horizontal } = this.props;
        const selectedTabId = this.props.selectedTabId && ObservableLike.getValue(this.props.selectedTabId);
        let childrenCount = 0;
        let childIndex = 0;
        const idToIndex: { [id: string]: number } = {};
        let defaultId: string | undefined;
        React.Children.map(this.props.children, (child: React.ReactElement<ITabProps>) => {
            if (child && child.type === Tab) {
                idToIndex[child.props.id] = childIndex++;
                childrenCount++;
                if (!defaultId && child.props.id) {
                    defaultId = "tab-" + child.props.id;
                }
            }
        });

        const children = React.Children.map(this.props.children, (child: React.ReactElement<ITabProps>) => {
            if (child && typeof child.type !== "string") {
                // We don't want to pass along the following props, unless the
                // child is a React component that will presumably know what
                // to do with them.

                const isSelected = (child.props.id && child.props.id.toLocaleLowerCase()) === (selectedTabId && selectedTabId.toLocaleLowerCase());
                const onClick = this.onTabClick;

                return React.cloneElement<ITabProps>(child, {
                    ...child.props,
                    isSelected,
                    onClick,
                    index: idToIndex[child.props.id],
                    setSize: childrenCount
                });
            }
            return child;
        });

        const orientationClass = orientation === Orientation.Vertical ? "flex-column" : "flex-row";
        const focusZoneDirection: FocusZoneDirection =
            orientation === Orientation.Vertical ? FocusZoneDirection.Vertical : FocusZoneDirection.Horizontal;
        return (
            <FocusZone direction={focusZoneDirection} focusGroupProps={{ defaultElementId: defaultId }}>
                <div aria-label={this.props.ariaLabel} className={css(this.props.className, "bolt-tabs", orientationClass, tabSize)} role="tablist">
                    {children}
                </div>
            </FocusZone>
        );
    }

    private onTabClick = (newTabId: string) => {
        const { onSelectedTabChanged } = this.props;

        if (onSelectedTabChanged) {
            onSelectedTabChanged(newTabId);
        }
    };

    private onSelectedTabIdChanged = () => {
        this.forceUpdate();
    };
}
